import { useState, useEffect, useMemo, useCallback, useDeferredValue, useRef } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { Button, message, Spin, Modal, Descriptions, Tag, Drawer } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, DeleteOutlined, CopyOutlined, AuditOutlined, HistoryOutlined, StopOutlined } from '@ant-design/icons';
import useAuth from '../../../../hooks/useAuth';
import ProformaControlsPanel from './ProformaControlsPanel';
import ProformaCustomerSection from './ProformaCustomerSection';
import ProformaLineGrid from './ProformaLineGrid';
import ProformaBottomPanel from './ProformaBottomPanel';
import ProformaSummaryModal from './ProformaSummaryModal';
import { proformaApi, loadProformaDropdownsSafe } from './proformaApi';
// Stage 6PL-1e — decision D3: on a customer swap, ask, but only when it would
// actually change a rate.
import { resolvePricesSafe } from '../../masters/priceLists/priceListApi';
import { buildRepriceDiff, applyReprice } from './priceRepricing';
import RepriceCustomerModal from './RepriceCustomerModal';
// Sales Tax Invoice reuse (Phase 1B): the SAME editor drives an invoice when
// mode="invoice". Proforma is the default and unchanged.
import { invoiceApi } from '../invoice/invoiceApi';
import {
  invoiceStatusUi, invoiceStatusLabel, invoiceEditorActions,
  releaseStatusTag, eInvoiceStatusLabel, postErrorMessage, cancelErrorMessage, formatMoney as fmtMoney
} from '../invoice/invoiceHelpers';
import InvoiceEventsTimeline from '../invoice/InvoiceEventsTimeline';
import CancelPostedInvoiceModal from '../invoice/CancelPostedInvoiceModal';
// Stage 6CM.1 — seed new proformas from Company Defaults first; this loader
// falls back to Proforma Settings (Stage 6Q.3) and then safe defaults.
import { loadProformaEffectiveDefaultsSafe } from '../../companyDefaults/companyDefaultsApi';
import { loadProformaGridSettingsSafe } from '../../masters/proformaGridSettings/proformaGridSettingsApi';
import { computeProformaPreview, buildSavePayload, blankLine, padLines, hydrateLines, statusLabel, proformaStatusUi, proformaEditorActions, enforceAreaUom, validateSalespersonForSave, salespersonStateFromApi, shouldBlockNavigation } from './proformaHelpers';
import ProformaPrintButton from './ProformaPrintButton';
import { validateCustomerSection } from './proformaCustomerValidation';

const MIN_ROWS = 12; // GlassMaster-style "lots of blank rows" spreadsheet feel.

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
function todayIso() { return new Date().toISOString().slice(0, 10); }

function emptyHeader() {
  return {
    id: null, proformaNo: '', status: 'PENDING', proformaDate: todayIso(),
    customerId: null, billTo: {}, shipTo: {},
    placeOfSupplyStateCode: null, companyStateCode: null,
    wastageMode: 'ROUND_OFF', wastageWidthMm: 0, wastageHeightMm: 0,
    inputUnitCode: 'MM', priceUomCode: 'SQF', areaMode: 'SQFT',
    // GlassMaster-style display controls (UI-only; drive grid columns/feel).
    areaVisible: ['SQFT'], otherFab: false, showLocation: false, groupByLoc: false,
    isSez: false, isExport: false, editChargeable: false,
    // Edgework RFT/RMT per-side length basis (from Company Defaults; CHARGE safe default).
    edgeworkDimensionBasis: 'CHARGE',
    // Bottom-panel session overrides (UI-only): per-document fabrication rate
    // overrides + entered document-level other-charge values.
    fabRates: {}, otherChargeValues: {},
    // Document-level discount (before tax) — % drives the amount, or a flat ₹.
    docDiscountPercent: 0, docDiscountAmount: 0,
    // Free-text operator remarks saved with the proforma (bottom-panel tabs).
    proformaRemarks: '', workOrderRemarks: '',
    // Free-text PROJECT reference (a.k.a. "Project Ref" in the details popup).
    projectName: '',
    // Migration 0185 — this document's own price-list choice.
    priceListMode: 'AUTO',
    priceListOverrideId: null,
    // Order-detail metadata (Details & Summary popup).
    templatesReceived: '', poNo: '', poDate: '',
    dispatchRoute: null, dispatchMode: null, workType: null, paymentTerms: null,
    // Express-delivery flag + 0-5 priority rating (enabled only when checked).
    expressDelivery: false, expressDeliveryRating: 0
  };
}

export default function ProformaEditorPage({ mode = 'proforma' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  // ── Mode config. Proforma is the DEFAULT and behaves EXACTLY as before; the
  // invoice branch only swaps the api, paths, labels, status vocabulary and the
  // footer actions. All pricing/grid/GST logic is the shared engine, unchanged.
  const isInvoice = mode === 'invoice';
  const cfg = isInvoice
    ? {
        api: invoiceApi, listPath: '/erp/sales/invoices', basePath: '/erp/sales/invoices',
        noun: 'Invoice', listLabel: 'Invoice List', newLabel: 'New Tax Invoice', saveNewLabel: 'Save Invoice',
        statusUi: invoiceStatusUi, statusLabel: invoiceStatusLabel, editorActions: invoiceEditorActions
      }
    : {
        api: proformaApi, listPath: '/erp/sales/proformas', basePath: '/erp/sales/proformas',
        noun: 'Proforma', listLabel: 'PI List', newLabel: 'New Proforma', saveNewLabel: 'Save PI',
        statusUi: proformaStatusUi, statusLabel, editorActions: proformaEditorActions
      };

  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState(() => padLines([blankLine(1)], MIN_ROWS));
  const [dropdowns, setDropdowns] = useState({
    materialTypes: [], glassTypes: [], thicknesses: [], categories: [], gstRates: [],
    fabrications: [], processes: [], states: [], wastageModes: [], inputUnits: [], priceUoms: [], areaModes: [], errors: null
  });
  const [ddLoading, setDdLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  // Invoice mode — activity drawer (POSTED/VOUCHER_POSTED timeline).
  const [eventsOpen, setEventsOpen] = useState(false);
  // Invoice mode — Cancel Posted Invoice modal (Phase 2C).
  const [cancelOpen, setCancelOpen] = useState(false);
  // Live auto Other-Charges reported up from the bottom panel's rule engine
  // ({ amount, gst, ready }) so the Details & Summary popup can reflect the
  // same running total. Display only — the OC engine itself is untouched.
  const [liveOtherCharges, setLiveOtherCharges] = useState({ amount: 0, gst: 0, ready: false });
  // Tenant-wide hidden grid columns (Proforma Grid Columns page). A view
  // preference — applies to BOTH new and existing documents.
  const [gridHiddenColumns, setGridHiddenColumns] = useState([]);
  // Stage 6CM.2 — effective Company-Defaults BEHAVIOUR flags (chargeable mode,
  // round-off, other-charge auto-apply + runtime-override lock). Loaded for BOTH
  // new and existing documents and merged into the compute/grid/panel header so
  // the preview + cell-lock match what a save persists. These are NEVER written
  // into saved dims and are re-resolved authoritatively by the backend on save.
  const [effectiveFlags, setEffectiveFlags] = useState(null);

  // Stage SALES-PROFORMA-STATUS-1 — status-driven editability + action buttons.
  const statusUi = cfg.statusUi(header.status);
  const readOnly = statusUi.readOnly;
  // Stage SALES-PROFORMA-UI-FLOW-1 — which action buttons show (new → Save PI;
  // existing editable → Update + Delete; CONFIRMED → Hold/Cancel; CANCELLED →
  // read-only). Delete is additionally gated on the delete permission.
  const actions = cfg.editorActions(header, me && me.permissions);
  const ddError = dropdowns.errors && (dropdowns.errors.all || null);
  // Stage SALES-PROFORMA-APPROVAL-1 — the status-change (confirm / hold / cancel /
  // reopen) actions live on the Proforma LIST drawer's action panel now, not in
  // the editor. The editor is a pure document editor (Save / Update / Delete /
  // Print). Approval + Work-Order readiness are surfaced from the list + inbox.

  // ── Fix 4 (SALES-PROFORMA-AUDIT-FIX-1) — unsaved-changes tracking ──
  // `dirty` flips true only on a genuine USER edit (child editors get the
  // wrapped setters below; programmatic hydration — load, company-defaults
  // seed, salesperson auto-resolve — uses the raw setters and never dirties).
  // A `hydratedRef` gate ignores any edit until the initial load/seed settles,
  // and `dirtyRef` avoids re-rendering on every keystroke once already dirty.
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const hydratedRef = useRef(false);
  const markDirty = useCallback(() => {
    if (!hydratedRef.current) return;
    if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); }
  }, []);
  const clearDirty = useCallback(() => { dirtyRef.current = false; setDirty(false); }, []);
  const setHeaderDirty = useCallback((u) => { markDirty(); setHeader(u); }, [markDirty]);
  const setLinesDirty = useCallback((u) => { markDirty(); setLines(u); }, [markDirty]);
  // Enable dirty tracking only after the initial load/seed settles. For an
  // existing doc this waits out `loading`; for a new doc it fires on mount.
  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => { hydratedRef.current = true; }, 0);
    return () => clearTimeout(t);
  }, [loading]);

  // Stage 6PL-1c — the dropdown payload is CUSTOMER-SPECIFIC: the fabrication
  // and #process rates in it are the ones this customer negotiated. So it is
  // refetched whenever the customer or the document date changes, not once on
  // mount. Without that, picking the customer AFTER typing a line would leave
  // the editor quoting master rates for the rest of the session — the ordering
  // hazard recorded as R1 in the stage plan.
  //
  // Rates already committed onto typed lines are NOT rewritten here. Moving
  // money on a document the operator is looking at has to be asked for, not
  // done silently (decision D3), and the diff-and-prompt lands in PL-1e.
  const ddCustomerId = header.customerId || null;
  const ddDate = header.proformaDate || null;
  // Migration 0185 — the overlay must follow the DOCUMENT's card, so the
  // fabrication/process columns and the badge agree with what the lines will
  // actually be priced at.
  const ddMode = header.priceListMode || 'AUTO';
  const ddOverride = header.priceListOverrideId || null;
  useEffect(() => {
    let cancelled = false;
    setDdLoading(true);
    loadProformaDropdownsSafe({
      customerId: ddCustomerId, date: ddDate,
      priceListMode: ddMode, priceListOverrideId: ddOverride
    })
      .then((d) => { if (!cancelled) setDropdowns(d); })
      .finally(() => { if (!cancelled) setDdLoading(false); });
    return () => { cancelled = true; };
  }, [ddCustomerId, ddDate, ddMode, ddOverride]);

  // ── Stage 6PL-1e — the customer-swap re-price prompt (decision D3) ──
  //
  // The rule is "ask ONLY when it would actually change a rate". A prompt that
  // fires on every customer pick trains the operator to dismiss it unread, and
  // is then worth nothing on the day it matters.
  //
  // Four things must NOT trigger it, and each is a separate guard below:
  //   • the initial render (there is no "previous" customer yet)
  //   • hydrating a SAVED document (its customer is being restored, not changed)
  //   • a read-only document (nothing may move on it at all)
  //   • an empty / un-priced document (there is nothing to re-price)
  // and finally an empty diff stays silent even after all of that.
  const [repriceDiff, setRepriceDiff] = useState(null);
  const prevCustomerRef = useRef(undefined);
  // Read through a ref so the effect depends ONLY on the customer: it must fire
  // when the customer changes, never when a line is edited.
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const headerRef = useRef(header);
  headerRef.current = header;

  useEffect(() => {
    // The trigger is the PRICING IDENTITY of the document, not just its
    // customer: changing the card (or turning cards off) changes rates exactly
    // as changing the customer does, and must ask in the same way.
    const cid = `${header.customerId || ''}|${header.priceListMode || 'AUTO'}|${header.priceListOverrideId || ''}`;
    const prev = prevCustomerRef.current;
    prevCustomerRef.current = cid;
    if (prev === undefined) return undefined;      // first render
    if (!hydratedRef.current) return undefined;    // a saved document is loading
    if (prev === cid) return undefined;
    if (readOnly) return undefined;

    const h = headerRef.current;
    const rows = linesRef.current || [];
    // EVERY line that names an item — deliberately NOT `salesRate > 0`. A
    // negotiated 0.00 is a real rate (a free replacement pane), and dropping
    // those refs here would leave them unresolved below, so the diff would
    // propose the MASTER rate instead of the new customer's own rate for that
    // item. A blank row carries no itemId, so this still means "nothing typed".
    const withItems = rows.filter((l) => l && l.itemId);
    if (!withItems.length) return undefined;       // nothing typed yet

    let alive = true;
    const refs = [...new Set(withItems.map((l) => String(l.itemId)))]
      .map((refId) => ({ kind: 'ITEM', refId }));
    resolvePricesSafe({
      customerId: cid, asOfDate: h.proformaDate || null, refs,
      // Migration 0185 — ask about the card THIS document uses, not the
      // customer's. Otherwise switching a document to "none" would diff
      // against the customer's card and propose no change at all.
      forceListId: h.priceListMode === 'FIXED' ? (h.priceListOverrideId || null) : null,
      disabled: h.priceListMode === 'NONE'
    })
      .then((r) => {
        if (!alive) return;
        const diff = buildRepriceDiff({
          lines: linesRef.current, rates: r.rates, docPriceUom: h.priceUomCode
        });
        if (diff.length) setRepriceDiff(diff);     // silence when nothing moves
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header.customerId, header.priceListMode, header.priceListOverrideId]);

  // Stage 6CM.2 — load the effective behaviour flags once (new + existing docs).
  // Soft-fail: a null result leaves the engine on its legacy defaults.
  useEffect(() => {
    let cancelled = false;
    loadProformaEffectiveDefaultsSafe().then((s) => {
      if (cancelled || !s) return;
      setEffectiveFlags({
        chargeableMode:                  s.defaultChargeableMode || 'AUTO_FROM_WASTAGE',
        roundOffEnabled:                 s.defaultRoundOffEnabled !== false,
        roundOffMode:                    s.defaultRoundOffMode || 'NEAREST',
        autoApplyOtherCharges:           s.autoApplyOtherCharges !== false,
        allowRuntimeOtherChargeOverride: s.allowRuntimeOtherChargeOverride !== false
      });
    });
    return () => { cancelled = true; };
  }, []);

  // Stage 6Q.4 — tenant grid column visibility. Loaded for every document
  // (new + existing) since hiding a column is a pure view preference.
  useEffect(() => {
    let cancelled = false;
    loadProformaGridSettingsSafe().then((s) => {
      if (cancelled || !s) return;
      setGridHiddenColumns(Array.isArray(s.hiddenColumns) ? s.hiddenColumns : []);
    });
    return () => { cancelled = true; };
  }, []);

  // Stage 6Q.3 + 6CM.1 — seed a NEW proforma from the tenant-wide effective
  // defaults (Company Defaults first, then Proforma Settings, then safe
  // defaults — resolved server-side by /company-defaults/proforma-effective).
  // Edit mode (id present) loads the saved document instead, so this is
  // skipped there. Soft-fail: a null result just leaves the editor on its
  // built-in fallbacks. The DTO shape is unchanged, so the mapping below is
  // identical to before — only the source (+ editChargeable) is new.
  useEffect(() => {
    if (id) return undefined;
    let cancelled = false;
    loadProformaEffectiveDefaultsSafe().then((s) => {
      if (cancelled || !s) return;
      setHeader((h) => ({
        ...h,
        priceUomCode:    s.defaultPriceUomCode  || h.priceUomCode,
        inputUnitCode:   s.defaultInputUnitCode || h.inputUnitCode,
        wastageMode:     s.defaultWastageMode   || h.wastageMode,
        wastageWidthMm:  s.defaultWastageWidthMm  != null ? s.defaultWastageWidthMm  : h.wastageWidthMm,
        wastageHeightMm: s.defaultWastageHeightMm != null ? s.defaultWastageHeightMm : h.wastageHeightMm,
        // STRICT Price-UOM rule: the area unit is derived from the company Price
        // UOM (SQM ⇒ SQMT, SQF ⇒ SQFT); RFT/RMT preserved.
        areaVisible: enforceAreaUom(
          (Array.isArray(s.defaultAreaVisible) && s.defaultAreaVisible.length) ? s.defaultAreaVisible : h.areaVisible,
          s.defaultPriceUomCode || h.priceUomCode),
        otherFab:     s.defaultOtherFab === true,
        showLocation: s.defaultShowLocation === true,
        groupByLoc:   s.defaultGroupByLoc === true,
        companyStateCode: s.defaultCompanyStateCode || h.companyStateCode,
        // Stage 6CM.1 — Edit-Chargeable default from Company Defaults (safe
        // existing header field). Other Company-Defaults flags (round-off /
        // other-charge auto-apply) are returned but their CONSUMPTION is
        // deferred to Stage 6CM.2 to avoid touching the calculation engine.
        editChargeable: s.defaultEditChargeable != null
          ? (s.defaultEditChargeable === true) : h.editChargeable,
        // Edgework RFT/RMT per-side length basis (CHARGE | ACTUAL) from Company
        // Defaults; drives the edge picker + the on-screen value-add preview.
        edgeworkDimensionBasis: String(s.edgeworkDimensionBasis || h.edgeworkDimensionBasis || 'CHARGE').toUpperCase()
      }));
    });
    return () => { cancelled = true; };
  }, [id]);

  const loadExisting = useCallback(async (pid) => {
    setLoading(true);
    try {
      const data = await cfg.api.get(pid);
      setHeader({
        id: data.id, proformaNo: isInvoice ? data.invoiceNo : data.proformaNo, status: data.status,
        proformaDate: isInvoice ? data.invoiceDate : data.proformaDate, customerId: data.customerId,
        billTo: data.billToSnapshot || {}, shipTo: data.shipToSnapshot || {},
        placeOfSupplyStateCode: data.placeOfSupplyStateCode, companyStateCode: data.companyStateCode,
        wastageMode: data.wastageMode, wastageWidthMm: data.wastageWidthMm, wastageHeightMm: data.wastageHeightMm,
        inputUnitCode: data.inputUnitCode || 'MM', priceUomCode: data.priceUomCode || 'SQF', areaMode: data.areaMode || 'SQFT',
        areaVisible: enforceAreaUom(Array.isArray(data.areaVisible) && data.areaVisible.length ? data.areaVisible : ['SQFT'], data.priceUomCode || 'SQF'),
        otherFab: data.otherFab === true, showLocation: data.showLocation === true, groupByLoc: data.groupByLoc === true,
        isSez: false, isExport: false, editChargeable: data.editChargeable === true,
        // Edgework basis is a company-wide rule (not stored per-doc) — default
        // CHARGE now; the effective value is applied just below.
        edgeworkDimensionBasis: 'CHARGE',
        fabRates: {}, otherChargeValues: (data.otherChargeOverrides && typeof data.otherChargeOverrides === 'object') ? data.otherChargeOverrides : {},
        docDiscountPercent: Number(data.docDiscountPercent) || 0, docDiscountAmount: Number(data.docDiscountAmount) || 0,
        manualOtherCharges: Array.isArray(data.manualOtherCharges) ? data.manualOtherCharges : [],
        proformaRemarks: data.proformaRemarks || '', workOrderRemarks: data.workOrderRemarks || '',
        projectName: data.projectName || '',
        priceListMode: data.priceListMode || 'AUTO',
        priceListOverrideId: data.priceListOverrideId || null,
        templatesReceived: data.templatesReceived || '', poNo: data.poNo || '', poDate: data.poDate || '',
        dispatchRoute: data.dispatchRoute || null, dispatchMode: data.dispatchMode || null,
        workType: data.workType || null, paymentTerms: data.paymentTerms || null,
        // Express-delivery flag + priority rating (persist across edits).
        expressDelivery: data.expressDelivery === true,
        expressDeliveryRating: Number(data.expressDeliveryRating) || 0,
        // Invoice mode — posted GL/release state (read-only display; harmless for PI).
        postedVoucherNo: data.postedVoucherNo || null,
        postedVoucherId: data.postedVoucherId || null,
        releaseStatus: data.releaseStatus || null,
        eInvoiceStatus: data.eInvoiceStatus || null,
        postedAt: data.postedAt || null,
        // Invoice mode — posted-cancellation (Phase 2C) read-only display.
        reversalVoucherNo: data.reversalVoucherNo || null,
        cancellationReason: data.cancellationReason || null,
        cancelledAt: data.cancelledAt || null,
        customerName: data.customerName || (data.billToSnapshot && (data.billToSnapshot.name || data.billToSnapshot.displayName)) || null,
        // WO-3.3 — carry the revision lineage + live Work Order state into header
        // so isChangeRevision can gate the "Release new size" action. Without these
        // header.revisionNo / header.workOrder are undefined and the button never shows.
        revisionNo: data.revisionNo, supersedesId: data.supersedesId,
        rootProformaId: data.rootProformaId, isCurrent: data.isCurrent,
        workOrder: data.workOrder || null,
        // Fix 2 (SALES-PROFORMA-AUDIT-FIX-1) — hydrate the persisted HRMS
        // salesperson so a manual override survives reopen → edit → save. The
        // mapper already returns these fields; without hydrating them the field
        // re-defaults to the customer mapping and the override is lost on re-save.
        ...salespersonStateFromApi(data)
      });
      // Edgework basis is company-wide, not persisted on the document — pull the
      // effective value so the edit-mode preview measures edges the same way.
      loadProformaEffectiveDefaultsSafe().then((s) => {
        if (s && s.edgeworkDimensionBasis) {
          setHeader((h) => ({ ...h, edgeworkDimensionBasis: String(s.edgeworkDimensionBasis).toUpperCase() }));
        }
      });
      // Reconstruct the editor tree (refUuid / parentRefUuid / level) from the
      // persisted line_no / parentLineNo / nestLevel, then pad for the blank feel.
      const mapped = hydrateLines(data.lines && data.lines.length ? data.lines : [blankLine(1)])
        .map((l) => ({ ...l, lineDiscount: l.lineDiscount || 0 }));
      setLines(padLines(mapped, MIN_ROWS));
    } catch (err) {
      message.error(backendError(err, 'Failed to load proforma.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (id) loadExisting(id); }, [id, loadExisting]);

  // WO-3.3 — release the freshly-added new-size line(s) of a Change-Order revision:
  // append their pieces to the same Work Order, then reload to reflect committed state.
  const doReleaseNewSize = useCallback(async () => {
    if (!header.id) return;
    try {
      setSaving(true);
      const res = await proformaApi.releaseNewSize(header.id);
      const n = res && res.released ? res.released.pieceCount : 0;
      message.success(`Released new size — ${n} piece(s) queued on the Work Order.`);
      await loadExisting(header.id);
    } catch (err) {
      message.error(backendError(err, 'Could not release the new size.'));
    } finally {
      setSaving(false);
    }
  }, [header.id, loadExisting]);
  // A released Change-Order revision (rev > 1 with a live WO) shows the "Release new
  // size" action; it must be saved first so the new line persists as LIVE/uncommitted.
  const isChangeRevision = Number(header.revisionNo) > 1 && !!(header.workOrder && header.workOrder.exists);

  // Toggle "edit chargeable": ONLY unlock the CHRG (MM) W/H columns. It does NOT
  // seed manual sizes — a blank manual size keeps following the regular wastage
  // workflow (and reacts to actual-size changes) even while the box is ticked.
  // The unlocked cell shows the auto charge size as a ghost placeholder; the
  // line is overridden only for cells the user actually types a value into.
  const toggleEditChargeable = useCallback((checked) => {
    setHeaderDirty((h) => ({ ...h, editChargeable: checked }));
  }, [setHeaderDirty]);

  // Stage 6CM.2 — merge the effective behaviour flags onto the header for compute
  // + grid + bottom panel (flags only; saved dims/UOM untouched). Absent → legacy.
  const effHeader = useMemo(
    () => (effectiveFlags ? { ...header, ...effectiveFlags } : { ...header }),
    [header, effectiveFlags]
  );
  // PERF: the live preview (full-document nesting bin-pack + roll-up + GST) is the
  // heaviest thing on the keystroke path — and its output rebuilds every row's
  // computed `view`, so a fresh recompute makes ALL visible grid rows re-render.
  // Price off a DEFERRED copy of `lines`: the urgent keystroke render keeps the
  // memo-cached preview (unchanged view refs → GridRow memo holds → only the
  // edited cell repaints, typing stays crisp), while the expensive recompute runs
  // in a low-priority, interruptible render. `lines` itself stays live everywhere
  // that matters — the grid's editable cells read live `lines`, and a save uses
  // live `lines` (buildSavePayload), so this changes scheduling only, never a
  // number. The deferred preview always converges to the final value.
  const deferredLines = useDeferredValue(lines);
  const preview = useMemo(() => computeProformaPreview({ header: effHeader, lines: deferredLines }), [effHeader, deferredLines]);
  // Stable header object for the grid — only changes when a header field or the
  // derived supply type changes, NOT on every line keystroke. A fresh object
  // each render would defeat the grid's per-row view cache (1200-row freeze).
  const gridHeader = useMemo(() => ({ ...effHeader, supplyType: preview.supplyType, hiddenGridColumns: gridHiddenColumns, jumboCondition: dropdowns.jumboCondition || null }), [effHeader, preview.supplyType, gridHiddenColumns, dropdowns.jumboCondition]);

  const doSave = useCallback(async () => {
    const payload = buildSavePayload(header, lines);
    // Invoice mode: the backend keys the document date as invoiceDate (it maps
    // invoiceDate → the engine's proformaDate internally). Send both.
    if (isInvoice) payload.invoiceDate = payload.proformaDate;
    if (!payload.proformaDate) { message.warning(`${cfg.noun} date is required.`); return null; }
    if (payload.lines.length === 0) { message.warning('Add at least one line.'); return null; }
    // Bill To / Ship To required-field + format guard (Name, Addr 1, City,
    // Pincode 6-digit, Indian Mobile, GSTIN/PAN/Aadhaar/URP) — block save.
    const party = validateCustomerSection(header.billTo, header.shipTo);
    if (!party.ok) { message.error(party.errors[0]); return null; }
    // Salesperson is mandatory to save a PROFORMA (mirrors the backend). An
    // invoice has no such rule, so the check is skipped in invoice mode.
    if (!isInvoice) {
      const sp = validateSalespersonForSave(header);
      if (!sp.ok) { message.error(sp.error); return null; }
    }
    setSaving(true);
    try {
      const saved = header.id
        ? await cfg.api.update(header.id, payload)
        : await cfg.api.create(payload);
      message.success(header.id ? `${cfg.noun} saved.` : `${cfg.noun} created.`);
      setHeader((h) => ({
        ...h, id: saved.id,
        proformaNo: isInvoice ? saved.invoiceNo : saved.proformaNo,
        status: saved.status
      }));
      // Fix 4 — a successful save is the only thing that clears the dirty flag.
      clearDirty();
      if (!header.id) navigate(`${cfg.basePath}/${saved.id}`, { replace: true });
      return saved;
    } catch (err) {
      message.error(backendError(err, `Failed to save ${cfg.noun.toLowerCase()}.`));
      return null;
    } finally {
      setSaving(false);
    }
  }, [header, lines, navigate, clearDirty]);

  // Invoice mode only — abandon a DRAFT (draft-only backend guard). Frees the
  // source proforma. Not a delete, not the posted cancel.
  const doDiscard = useCallback(() => {
    if (!header.id) return;
    Modal.confirm({
      title: 'Discard this Tax Invoice draft?',
      content: 'This draft will be closed and cannot be edited again. If it was imported from a Proforma, that Proforma will become available for invoicing again.',
      okText: 'Discard draft', okType: 'danger', cancelText: 'Keep',
      onOk: async () => {
        setSaving(true);
        try {
          await invoiceApi.discard(header.id);
          message.success('Draft discarded.');
          clearDirty();
          navigate('/erp/sales/invoices');
        } catch (err) {
          message.error(backendError(err, 'Could not discard the draft.'));
        } finally {
          setSaving(false);
        }
      }
    });
  }, [header.id, navigate, clearDirty]);

  // Invoice mode only — POST a DRAFT to the ledger (Phase 2 GL posting). The
  // backend is authoritative: it validates the accounting period, creates/reuses
  // the sales voucher idempotently and freezes the document. This is a financial
  // action, so it is confirmed with a compact summary. On success we RELOAD from
  // the backend (never optimistically set posted) so the voucher + release state
  // are the persisted truth. The Modal's async onOk keeps the button in a loading
  // state and blocks a double-click; backend idempotency is the real safety net.
  const doPost = useCallback(() => {
    if (!header.id) return;
    const t = preview.totals || {};
    const gst = Number(t.cgstAmount || 0) + Number(t.sgstAmount || 0) + Number(t.igstAmount || 0);
    Modal.confirm({
      title: `Post Tax Invoice ${header.proformaNo || ''}?`.trim(),
      width: 460,
      okText: 'Post Invoice',
      cancelText: 'Cancel',
      content: (
        <div className="flex flex-col gap-2">
          <div className="text-xs text-slate-500">
            Posting will create the accounting entry and lock this Tax Invoice from further
            editing. Any later correction must follow the applicable cancellation, credit-note
            or debit-note workflow.
          </div>
          <Descriptions size="small" column={1} bordered
            styles={{ label: { width: 150 } }}
            items={[
              { key: 'cust', label: 'Customer', children: (header.billTo && (header.billTo.name || header.billTo.displayName)) || '—' },
              { key: 'tax', label: 'Taxable Value', children: fmtMoney(t.taxableAmount) },
              { key: 'gst', label: 'GST', children: fmtMoney(gst) },
              { key: 'ro', label: 'Round Off', children: fmtMoney(t.roundOffAmount) },
              { key: 'gt', label: 'Grand Total', children: <strong>{fmtMoney(t.grandTotal)}</strong> }
            ]}
          />
        </div>
      ),
      onOk: async () => {
        try {
          await invoiceApi.post(header.id);
          message.success('Tax Invoice posted successfully. Accounting voucher created.');
          await loadExisting(header.id); // reload → posted state + voucher, from backend
        } catch (err) {
          const code = err && err.response && err.response.data && err.response.data.error && err.response.data.error.code;
          message.error(postErrorMessage(code, backendError(err, 'Could not post the Tax Invoice.')));
          throw err; // keep the modal open on failure
        }
      }
    });
  }, [header.id, header.proformaNo, header.billTo, preview.totals, loadExisting]);

  // Invoice mode only — CANCEL a POSTED invoice (Phase 2C). Reverses the
  // accounting voucher via the backend (authoritative); we only capture the
  // mandatory reason (in the shared modal) and RELOAD on success so the cancelled
  // state + reversal info are the persisted truth. Backend error CODES are mapped
  // to clear copy; the modal stays open on failure.
  const doCancelPosted = useCallback(async (reason) => {
    if (!header.id) return;
    try {
      await invoiceApi.cancel(header.id, reason);
      message.success('Tax Invoice cancelled. The accounting voucher was reversed.');
      setCancelOpen(false);
      await loadExisting(header.id);
    } catch (err) {
      const code = err && err.response && err.response.data && err.response.data.error && err.response.data.error.code;
      message.error(cancelErrorMessage(code, backendError(err, 'Could not cancel the Tax Invoice.')));
      // keep the modal open so the user can retry / read the reason
    }
  }, [header.id, loadExisting]);

  // Stage SALES-PROFORMA-UI-FLOW-4 — Save PI / Update open the Order Details &
  // Summary window (see the footer buttons). The actual save is confirmed from
  // inside that window via saveFromSummary(): it runs the same doSave() (so all
  // validation still fires and surfaces there, beside the salesperson field) and
  // only closes the window once the save succeeds.
  const saveFromSummary = useCallback(async () => {
    const saved = await doSave();
    if (saved) setSummaryOpen(false);
  }, [doSave]);

  // Stage SALES-PROFORMA-UI-FLOW-1 — soft delete a saved editable PI. Confirms,
  // calls DELETE (backend sets deleted_at), clears the dirty guard, returns to
  // the list. Only rendered for PENDING/HOLD with the delete permission.
  const doDelete = useCallback(() => {
    if (!header.id) return;
    Modal.confirm({
      title: 'Delete Proforma?',
      content: 'This will remove the Proforma from active lists. This action uses soft delete.',
      okText: 'Yes, Delete', okType: 'danger', cancelText: 'No',
      onOk: async () => {
        setSaving(true);
        try {
          await proformaApi.remove(header.id);
          message.success('Proforma deleted.');
          clearDirty();   // don't let the unsaved-change blocker prompt after delete
          navigate('/erp/sales/proformas');
        } catch (err) {
          message.error(backendError(err, 'Failed to delete proforma.'));
        } finally {
          setSaving(false);
        }
      }
    });
  }, [header.id, navigate, clearDirty]);

  // Stage SALES-PROFORMA-CLONE-1 — duplicate this saved proforma into a new
  // PENDING draft and open it. The copy is made server-side from the LAST SAVED
  // version, so if there are unsaved edits they are neither copied nor kept —
  // the warning is explicit. Available for any saved status (needs create perm).
  const doClone = useCallback(() => {
    if (!header.id) return;
    const pf = header.proformaNo || 'this proforma';
    Modal.confirm({
      title: 'Duplicate proforma',
      content: dirty
        ? 'You have unsaved changes. Duplicating copies the last SAVED version and will discard your current unsaved edits. Save first if you want them included. Continue?'
        : `Create a new editable draft copied from ${pf}? All line items and details are copied; the new proforma starts as Pending with a fresh number.`,
      okText: 'Duplicate', cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        try {
          const created = await proformaApi.clone(header.id);
          message.success(`Created ${created && created.proformaNo ? created.proformaNo : 'a copy'} from ${pf}.`);
          clearDirty();   // don't let the unsaved-change blocker prompt on navigate
          if (created && created.id) navigate(`/erp/sales/proformas/${created.id}`);
        } catch (err) {
          message.error(backendError(err, 'Could not duplicate the proforma.'));
        } finally {
          setSaving(false);
        }
      }
    });
  }, [header.id, header.proformaNo, dirty, navigate, clearDirty]);

  // Fix 4 — block in-app route navigation (sidebar, PI-List button, browser
  // back) while there are unsaved edits, and prompt to confirm. The router is a
  // data router (createBrowserRouter), so useBlocker is available.
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        shouldBlockNavigation({ dirty, saving, readOnly })
        && currentLocation.pathname !== nextLocation.pathname,
      [dirty, saving, readOnly]
    )
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    Modal.confirm({
      title: 'Discard unsaved changes?',
      content: 'This proforma has unsaved changes that will be lost. Leave without saving?',
      okText: 'Leave', okType: 'danger', cancelText: 'Stay',
      onOk: () => blocker.proceed(),
      onCancel: () => blocker.reset()
    });
  }, [blocker]);

  // Fix 4 — warn on browser tab close / refresh / external navigation.
  useEffect(() => {
    const handler = (e) => {
      if (!shouldBlockNavigation({ dirty, saving, readOnly })) return;
      e.preventDefault();
      e.returnValue = '';   // Chrome requires returnValue to be set.
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, saving, readOnly]);

  return (
    <Spin spinning={loading}>
      {/* pb-16 keeps the last content clear of the sticky footer action bar. */}
      <div className="flex flex-col gap-2 pb-16">
        {/* Stage SALES-PROFORMA-UI-FLOW-3: the top identity strip was removed —
            the PF number, status and row/qty counts live in the footer action
            bar. Document info starts directly with the header strip below. */}

        {/* ── compact header strip: New Proforma | Bill To | Ship To ── */}
        <div className="flex flex-wrap bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <ProformaControlsPanel
            header={header} setHeader={setHeaderDirty} dropdowns={dropdowns}
            dropdownsLoading={ddLoading} dropdownsError={ddError} readOnly={readOnly}
            onToggleEditChargeable={toggleEditChargeable}
          />
          <ProformaCustomerSection
            header={header} setHeader={setHeaderDirty} dropdowns={dropdowns}
            dropdownsLoading={ddLoading} dropdownsError={ddError} readOnly={readOnly}
          />
        </div>

        {/* Salesperson (auto-mapped), project ref, P.O + dispatch details and
            the financial summary now live in the "Details & Summary" popup,
            opened from the bottom action bar. */}

        {/* ── the spreadsheet is the main focus ── */}
        <ProformaLineGrid
          lines={lines} setLines={setLinesDirty} header={gridHeader}
          dropdowns={dropdowns} readOnly={readOnly}
          pricedRows={preview.lines}
        />

        {/* ── GlassMaster-style bottom tabs: Other Charges / Summary ── */}
        <ProformaBottomPanel
          lines={lines} setLines={setLinesDirty} header={effHeader} setHeader={setHeaderDirty}
          dropdowns={dropdowns} pricedLines={preview.lines}
          totals={preview.totals} supplyType={preview.supplyType}
          readOnly={readOnly}
          onOtherChargesChange={setLiveOtherCharges}
        />

        {isInvoice && String(header.status).toLowerCase() === 'posted' ? (
          <div className="px-1">
            <Descriptions
              size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}
              title={
                <span className="flex items-center gap-2">
                  <Tag color="green">POSTED</Tag>
                  <span className="text-xs font-normal text-slate-500">
                    This Tax Invoice is posted to the ledger and is read-only.
                  </span>
                </span>
              }
              items={[
                { key: 'v', label: 'Accounting Voucher', children: header.postedVoucherNo || '—' },
                { key: 'r', label: 'Release', children: (() => { const t = releaseStatusTag(header.releaseStatus); return <Tag color={t.color}>{t.label}</Tag>; })() },
                { key: 'e', label: 'E-Invoice', children: eInvoiceStatusLabel(header.eInvoiceStatus) },
                { key: 'p', label: 'Posted At', children: header.postedAt ? new Date(header.postedAt).toLocaleString('en-IN') : '—' }
              ]}
            />
            {String(header.releaseStatus || '').toUpperCase() === 'BLOCKED_IRN' ? (
              <div className="mt-1 text-xs text-amber-700">
                Accounting is posted, but this invoice is <strong>blocked for issue</strong> until its
                IRN (e-Invoice) is generated. It is not yet ready to dispatch.
              </div>
            ) : null}
          </div>
        ) : isInvoice && String(header.status).toLowerCase() === 'cancelled' ? (
          <div className="px-1">
            <Descriptions
              size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}
              title={
                <span className="flex items-center gap-2">
                  <Tag color="red">CANCELLED</Tag>
                  <span className="text-xs font-normal text-slate-500">
                    This Tax Invoice was cancelled and its accounting voucher reversed. It is read-only.
                  </span>
                </span>
              }
              items={[
                { key: 'v', label: 'Original Voucher', children: header.postedVoucherNo || '—' },
                { key: 'rv', label: 'Reversal Voucher', children: header.reversalVoucherNo || 'Reversed in place' },
                { key: 'ca', label: 'Cancelled At', children: header.cancelledAt ? new Date(header.cancelledAt).toLocaleString('en-IN') : '—' },
                { key: 'rn', label: 'Reason', span: 3, children: header.cancellationReason || '—' }
              ]}
            />
          </div>
        ) : readOnly ? (
          <div className="text-xs text-slate-500 px-1">
            This {cfg.noun.toLowerCase()} is {cfg.statusLabel(header.status)} and is read-only.
          </div>
        ) : null}

        {/* ── sticky footer action bar (Stage SALES-PROFORMA-UI-FLOW-2) ──
             Document actions only (Print / Delete / Save / Update). The status
             lifecycle (Confirm / Hold / Cancel / reopen) lives on the
             Proforma LIST drawer's action panel now (Stage APPROVAL-1). Sticky
             to the viewport bottom; the container's pb-16 keeps content clear. */}
        <div className="pf-editor-actions sticky bottom-0 z-20 flex flex-wrap items-center gap-2 bg-white border-t border-slate-200 px-3 py-2 shadow-[0_-2px_8px_rgba(15,23,42,0.06)]">
          {/* Left rail — PI List (respects the unsaved-change blocker) + state. */}
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={isInvoice ? () => navigate('/erp/sales/invoices') : () => navigate('/erp/sales/proformas')}>{isInvoice ? 'Invoice List' : 'PI List'}</Button>
          <span className="text-xs font-medium text-slate-500">
            {header.status ? cfg.statusLabel(header.status) : (header.id ? '' : (isInvoice ? 'New Tax Invoice' : 'New Proforma'))}
          </span>
          {dirty ? (
            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
              Unsaved changes
            </span>
          ) : null}
          <span className="text-xs text-slate-400 tabular-nums hidden md:inline">
            {lines.length} rows · {preview.totals.lineCount} with qty
          </span>

          <span className="flex-1 min-w-[8px]" />

          {/* Right rail — document actions only (status lifecycle lives on the
              list drawer now, Stage APPROVAL-1). */}
          {actions.print ? (
            <ProformaPrintButton proforma={{ id: header.id }} size="small" />
          ) : null}
          {actions.clone ? (
            <Button size="small" icon={<CopyOutlined />} loading={saving} onClick={doClone}>Duplicate</Button>
          ) : null}
          {actions.delete ? (
            <Button size="small" danger icon={<DeleteOutlined />} loading={saving} onClick={doDelete}>Delete</Button>
          ) : null}
          {isInvoice && actions.discard ? (
            <Button size="small" danger icon={<DeleteOutlined />} loading={saving} onClick={doDiscard}>Discard draft</Button>
          ) : null}
          {isInvoice && header.id ? (
            <Button size="small" icon={<HistoryOutlined />} onClick={() => setEventsOpen(true)}>View activity</Button>
          ) : null}
          {isInvoice && actions.post ? (
            <Button
              size="small" type="primary" icon={<AuditOutlined />}
              disabled={dirty}
              title={dirty ? 'Save your changes before posting.' : 'Post this Tax Invoice to the ledger'}
              onClick={doPost}
            >
              Post Tax Invoice
            </Button>
          ) : null}
          {isInvoice && actions.postedCancel ? (
            <Button
              size="small" danger icon={<StopOutlined />}
              title="Cancel this posted Tax Invoice (reverses the accounting voucher)"
              onClick={() => setCancelOpen(true)}
            >
              Cancel Posted Invoice
            </Button>
          ) : null}
          {actions.update ? (
            <Button size="small" type="primary" loading={saving} icon={<SaveOutlined />} onClick={() => setSummaryOpen(true)}>Update</Button>
          ) : null}
          {isChangeRevision ? (
            <Button
              size="small"
              loading={saving}
              disabled={dirty}
              title={dirty ? 'Save the new-size line first' : 'Append the new-size line(s) pieces to the Work Order'}
              onClick={doReleaseNewSize}
            >
              ▶ Release new size
            </Button>
          ) : null}
          {actions.saveNew ? (
            <Button size="small" type="primary" loading={saving} icon={<SaveOutlined />} onClick={() => setSummaryOpen(true)}>{isInvoice ? 'Save Invoice' : 'Save PI'}</Button>
          ) : null}
        </div>

        {/* Stage 6PL-1e (D3) — only mounted when a rate would ACTUALLY move. */}
        <RepriceCustomerModal
          open={!!repriceDiff} diff={repriceDiff}
          customerName={(header.billTo && header.billTo.name) || null}
          onKeep={() => setRepriceDiff(null)}
          onApply={() => {
            // markDirty via setLinesDirty: re-pricing IS an edit, and leaving
            // the document "clean" would let it be navigated away from without
            // the unsaved-changes guard firing.
            setLinesDirty((rows) => applyReprice(rows, repriceDiff));
            setRepriceDiff(null);
          }} />

        <ProformaSummaryModal open={summaryOpen} onClose={() => setSummaryOpen(false)}
          header={header} setHeader={setHeaderDirty} setHeaderRaw={setHeader} markDirty={markDirty}
          readOnly={readOnly}
          totals={preview.totals} supplyType={preview.supplyType}
          otherCharges={liveOtherCharges}
          onSave={saveFromSummary} saving={saving} />

        {isInvoice ? (
          <Drawer title="Invoice activity" width={460} open={eventsOpen}
            onClose={() => setEventsOpen(false)} destroyOnClose>
            {header.id ? <InvoiceEventsTimeline invoiceId={header.id} /> : null}
          </Drawer>
        ) : null}

        {isInvoice ? (
          <CancelPostedInvoiceModal
            open={cancelOpen}
            invoice={{
              invoiceNo: header.proformaNo, customerName: header.customerName,
              grandTotal: preview.totals && preview.totals.grandTotal, postedVoucherNo: header.postedVoucherNo
            }}
            onClose={() => setCancelOpen(false)}
            onConfirm={doCancelPosted}
          />
        ) : null}
      </div>
    </Spin>
  );
}
